// ═══════════════════════════════════════════════════════════════
// Registro de dashboards del módulo Tienda Nube (F2-T3).
//
// Shape de entrada: el contrato del agregador neutro — ver la cabecera de
// `mercadolibre/registry.js` (no se re-documenta acá).
//
// LISTA BLANCA (ADR 2026-08-16, decisión 4): a TN solo entran dashboards
// cuyos hooks leen colecciones canónicas FILTRANDO por canal/conexión. El
// censo hook por hook (16-ago-2026, compuerta 1 de T3) dejó afuera a los
// dashboards de ML porque (a) hardcodean canal ML en componentes/hooks, o
// (b) dependen de rollups NO particionados por canal (docId
// `{empresaId}__{dominio}`) — mostrarían números ML bajo etiqueta TN.
//
// Los DOS dashboards de la lista son pantallas PROPIAS de TN sobre la misma
// `ordenes` canónica con `canal` literal: Medios de pago desde el 31-ago, y
// Ventas desde el ADR 2026-09-02 (ahí murió el wrapper que montaba la página
// de Ventas de ML con canal='tienda-nube' — los dashboards TN se diseñan por
// la data de TN, no como copia de ML). Lo compartido de verdad se IMPORTA
// (hooks de rango, `kpisVentas` del diccionario), nunca se re-monta.
//
// `bajada` (30-sep-2026): el objetivo del dashboard dicho al vendedor, en una
// línea y con el verbo adelante (vara: la guía de contenido de Shopify; entre
// 60 y 95 caracteres, para que ocupe dos renglones en la tarjeta). Es lo que muestra la TARJETA del hub; `descripcion` queda
// como el detalle de la cabecera al abrirlo. Sin `bajada`, la tarjeta muestra
// `descripcion` como siempre.
//
// `familia` (30-sep-2026): el ángulo al que pertenece cada dashboard; el hub
// los agrupa por familia en el orden de la cascada. Catálogo cerrado en
// `@core/lib/familiasDashboards`; `registry.test.js` exige que exista.
//
// Las cuatro patas de todo dashboard de canal viven en
// `.claude/patterns/dashboard-de-canal.md`. La lista la fija
// `registry.test.js`; ampliarla = sumar acá + al test.
// ═══════════════════════════════════════════════════════════════
import { ShoppingCart, CreditCard, Truck, Users, MapPin, Blocks, Megaphone, ShoppingBasket, TrendingUp, Tags, Ticket, Percent, Gauge, FolderTree, Calculator, LayoutGrid, LayoutDashboard, RotateCcw, Scale } from 'lucide-react'

export const DASHBOARDS_TN = [
  {
    // El Pulso general (4-oct-2026, diseño `claude/diseno-pulso-tn.md` v4): la
    // tienda en una sola vista, cada familia con su semáforo contra la misma
    // tienda antes, y el embudo. Era una tarjeta planeada; se muda acá con
    // pantalla. `soloStaff` hasta que se abra a clientes (ese día el hub lo
    // va a mostrar primero, porque ordena por familia). Entre Publicaciones
    // (-0.5) y Ventas (0).
    id: 'tn-pulso-general',
    nombre: 'Pulso general',
    bajada: 'Mirá toda la tienda de un vistazo y sabé a qué parte ir primero.',
    descripcion: 'El pulso de la tienda en una sola vista: cada familia con su semáforo contra la misma tienda antes, y el embudo de la tienda.',
    plataforma: 'tienda-nube',
    familia: 'general',
    tipo: 'informacion',
    icono: '📊',
    Icon: LayoutDashboard,
    component: () => import('./dashboards/PulsoTnDashboard.jsx'),
    orden: -0.25,
    soloStaff: true,
  },
  {
    id: 'tn-ventas',
    nombre: 'Ventas',
    bajada: 'Seguí tus ventas: cuánto facturás, qué se vende y si venís mejor que antes.',
    descripcion: 'Las ventas de tu tienda: cuánto se vendió y se cobró y cómo viene contra el período anterior, el listado con lo que se compró en cada venta, el ranking de qué se vende, la evolución y la distribución.',
    plataforma: 'tienda-nube',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '🛒',
    Icon: ShoppingCart,
    component: () => import('./dashboards/VentasTnDashboard.jsx'),
    orden: 0,
    // SIN `altoVolumen` — misma decisión que tn-medios-de-pago (abajo): la
    // justificación del chip ("mismo camino defendido que ml-ventas") murió
    // con el wrapper. La query sigue siendo rango server-side + CAP_VENTANA,
    // pero la pantalla propia agrupa logística y zonas en el cliente sobre la
    // ventana entera, y eso no se midió a 5.000 órdenes. Declararlo sería
    // afirmar algo que no medimos.
  },
  {
    id: 'tn-medios-de-pago',
    nombre: 'Medios de pago',
    bajada: 'Cobrá mejor: mirá cuánto se cobra, cuánto te cuesta cada medio y cuándo llega la plata.',
    descripcion: 'Cómo cobrás: qué parte de las ventas se cobra, cuánto cuesta cobrar con cada medio, cuánto tarda en acreditarse, la plata en camino y el estado de cada cobro.',
    plataforma: 'tienda-nube',
    familia: 'operacion',
    tipo: 'informacion',
    icono: '💳',
    Icon: CreditCard,
    component: () => import('./dashboards/MediosDePagoTnDashboard.jsx'),
    orden: 1,
    // SIN `altoVolumen` a propósito: la query es la misma que tn-ventas (rango
    // server-side + CAP_VENTANA), pero esta pantalla además agrupa y ordena en
    // el cliente sobre la ventana entera, y eso no se midió a 5.000 órdenes.
    // El chip no traba nada — declararlo sería afirmar algo que no medimos.
  },
  {
    id: 'tn-envios',
    nombre: 'Envíos',
    bajada: 'Entregá a tiempo: despachá lo que espera y mirá cuánto te cuesta el envío gratis.',
    descripcion: 'El ciclo de tus envíos: qué espera despacho, qué salió a tiempo y qué tarde, el seguimiento, cada transportista y depósito, y cuánto cuesta el envío gratis.',
    plataforma: 'tienda-nube',
    familia: 'operacion',
    tipo: 'informacion',
    icono: '🚚',
    Icon: Truck,
    component: () => import('./dashboards/EnviosTnDashboard.jsx'),
    orden: 2,
    // SIN `altoVolumen` — misma medición pendiente que las otras dos pantallas.
  },
  {
    id: 'tn-clientes',
    nombre: 'Clientes',
    bajada: 'Hacé que vuelvan: mirá quién compra por primera vez, quién repite y a quién escribirle.',
    descripcion: 'Quién te compra: quién compró por primera vez, quién volvió y quién lleva tiempo sin comprar, cuántos aceptan marketing, los que más compraron y cada cuánto vuelven.',
    plataforma: 'tienda-nube',
    familia: 'clientes',
    tipo: 'informacion',
    icono: '👥',
    Icon: Users,
    component: () => import('./dashboards/ClientesTnDashboard.jsx'),
    orden: 3,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    // 1-oct-2026: dejó de ser tarjeta planeada. Primera pantalla de la familia
    // Atención y postventa. Nace apagada en `vistasEmpresa`, como todo
    // dashboard nuevo: se prende en Plataforma → Vista detallada de empresa →
    // grupo "Dash Tienda Nube".
    id: 'tn-devoluciones',
    nombre: 'Devoluciones y cancelaciones',
    bajada: 'Perdé menos ventas: mirá qué se cancela y qué se devuelve, de qué producto y cuándo.',
    descripcion: 'Las ventas que se cayeron: qué se canceló y qué se devolvió, en qué momento (sin cobrar, ya cobrada, ya despachada), con qué motivo, y en qué producto, medio de pago, provincia o comprador se cae más.',
    plataforma: 'tienda-nube',
    familia: 'postventa',
    tipo: 'informacion',
    icono: '↩️',
    Icon: RotateCcw,
    component: () => import('./dashboards/DevolucionesTnDashboard.jsx'),
    orden: 3.5,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-geografia',
    nombre: 'Mapa de demanda',
    bajada: 'Mirá qué hacer en cada zona: dónde ya vendés, dónde pesa el envío y dónde se caen los carritos.',
    descripcion: 'Qué mirar en cada zona, contra toda la tienda: dónde ya vendés y con qué ticket, dónde el envío pesa en el ticket, dónde se caen los carritos y dónde se pide y no se cobra. Con el detalle de provincias, ciudades y códigos postales.',
    plataforma: 'tienda-nube',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '📍',
    Icon: MapPin,
    component: () => import('./dashboards/GeografiaTnDashboard.jsx'),
    orden: 4,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-combos',
    nombre: 'Combos y packs',
    bajada: 'Ampliá tu catálogo con combos de los productos que tus clientes ya compran juntos.',
    descripcion: 'Qué se compra junto: ventas multi-producto, productos por venta y los pares para armar packs.',
    plataforma: 'tienda-nube',
    familia: 'catalogo',
    tipo: 'informacion',
    icono: '🧩',
    Icon: Blocks,
    component: () => import('./dashboards/CombosTnDashboard.jsx'),
    orden: 5,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-pauta',
    nombre: 'Productos para pauta',
    bajada: 'Elegí qué pautar: lo que ya se vende, tiene stock y está listo para Google y Meta.',
    descripcion: 'Elegí qué pautar: lo que ya se vende, tiene stock y está listo para Google y Meta. Cada producto dice si se puede pautar hoy y, si no, qué lo frena: la ficha, el stock o el margen.',
    plataforma: 'tienda-nube',
    familia: 'trafico-conversion',
    tipo: 'informacion',
    icono: '📣',
    Icon: Megaphone,
    component: () => import('./dashboards/PautaTnDashboard.jsx'),
    orden: 6,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-carritos',
    nombre: 'Checkout y recupero',
    descripcion: 'La plata que quedó a un paso: carritos abandonados con causa medida, productos que se dejan, y el mail y link para recuperar cada uno.',
    plataforma: 'tienda-nube',
    familia: 'trafico-conversion',
    tipo: 'informacion',
    icono: '🛒',
    Icon: ShoppingBasket,
    component: () => import('./dashboards/CarritosTnDashboard.jsx'),
    orden: 7,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-rentabilidad',
    nombre: 'Rentabilidad por orden',
    bajada: 'Detectá cuánto ganás neto en cada orden, después de todos los costos.',
    descripcion: 'Cuánto te queda de cada venta, post-mortem y medido: renta neta por línea con el costo del catálogo, y lo no informado dicho como tal.',
    plataforma: 'tienda-nube',
    familia: 'rentabilidad',
    tipo: 'informacion',
    icono: '💰',
    Icon: TrendingUp,
    component: () => import('./dashboards/RentabilidadTnDashboard.jsx'),
    orden: 8,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-rentabilidad-inferida',
    nombre: 'Rentabilidad inferida',
    descripcion: 'Cuánto te dejaría lo que todavía no vendió, con los costos reales de tu tienda: qué está a pérdida antes de pautar, cuánto podés pagar por venta en publicidad y a qué precio llega a tu mínima.',
    plataforma: 'tienda-nube',
    familia: 'rentabilidad',
    tipo: 'informacion',
    icono: '🔮',
    Icon: Calculator,
    component: () => import('./dashboards/RentabilidadInferidaTnDashboard.jsx'),
    orden: 8.5,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-publicaciones',
    nombre: 'Publicaciones',
    bajada: 'Mejorá tu catálogo: revisá cada publicación y completá lo que le falta.',
    descripcion: 'Lo que tenés publicado en la tienda: estado, precio y stock reales, el vínculo al catálogo y el margen de cada publicación.',
    plataforma: 'tienda-nube',
    familia: 'catalogo',
    tipo: 'informacion',
    icono: '🏷️',
    Icon: Tags,
    component: () => import('./dashboards/PublicacionesTnDashboard.jsx'),
    // PRIMERO de los tableros TN (26-sep-2026, pedido de Mariano). -0.5 y no
    // -1: el nodo de la solapa `dash-tienda-nube` (config.js) usa -1 en Vista
    // detallada y un empate quedaría librado al orden del sort.
    orden: -0.5,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-cupones',
    nombre: 'Cupones',
    bajada: 'Sabé qué cupones funcionan: cuáles se usan, cuánto vendieron y cuánto descuento te costaron.',
    descripcion: 'Sabé qué cupones funcionan: cuáles se usan, cuánto vendieron y cuánto descuento te costaron. Cada cupón dice si rinde (trae cuentas nuevas o sube el ticket), y abajo está qué cupones te conviene tener: lo que ya tenés y lo que falta crear.',
    plataforma: 'tienda-nube',
    familia: 'precios-promociones',
    tipo: 'informacion',
    icono: '🎟️',
    Icon: Ticket,
    component: () => import('./dashboards/CuponesTnDashboard.jsx'),
    orden: 10,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-promociones',
    nombre: 'Promociones',
    bajada: 'Usá el descuento donde rinde: mirá qué promoción vendió y a qué productos conviene aplicarla.',
    descripcion: 'Usá el descuento donde rinde: mirá qué promoción vendió y a qué productos conviene aplicarla. Arriba, cuánto facturaste con promoción y cuánta plata en stock está quieta; en Oportunidades, qué hacer con cada producto según cuánto rota y si tiene margen.',
    plataforma: 'tienda-nube',
    familia: 'precios-promociones',
    tipo: 'informacion',
    icono: '💸',
    Icon: Percent,
    component: () => import('./dashboards/PromocionesTnDashboard.jsx'),
    orden: 11,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-matriz',
    nombre: 'Matriz de distribución de publicaciones',
    descripcion: 'Clasifica cada publicación en Estrella, Moderada, Eventual o Incógnita según su rendimiento contra el promedio, por unidades, vendido en productos o contribución.',
    plataforma: 'tienda-nube',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '🔲',
    Icon: LayoutGrid,
    component: () => import('./dashboards/MatrizDistribucionTnDashboard.jsx'),
    orden: 11.5,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-evolucion-matriz',
    nombre: 'Evolución de la matriz de publicaciones',
    descripcion: 'Cómo se movió cada publicación entre cuadrantes mes a mes: cuáles subieron, cuáles cayeron, cuáles se mantienen, y las que bajaron por falta de stock.',
    plataforma: 'tienda-nube',
    familia: 'ventas',
    tipo: 'informacion',
    icono: '📈',
    Icon: TrendingUp,
    component: () => import('./dashboards/EvolucionMatrizTnDashboard.jsx'),
    orden: 11.6,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-velocidad',
    nombre: 'Velocidad de consumo',
    bajada: 'Nivelá tu stock: reponé a tiempo lo que se agota y dejá de comprar lo que sobra.',
    descripcion: 'A qué ritmo se vende cada producto y cuántos días de stock le quedan: quebrados, críticos, bajos, ok y sobrados con el stock real de la tienda.',
    plataforma: 'tienda-nube',
    familia: 'abastecimiento',
    tipo: 'informacion',
    icono: '⏱️',
    Icon: Gauge,
    component: () => import('./dashboards/VelocidadConsumoTnDashboard.jsx'),
    orden: 12,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
  {
    id: 'tn-rubros',
    nombre: 'Rubros',
    bajada: 'Ordená tu catálogo en rubros para que tus clientes encuentren lo que buscan.',
    descripcion: 'Cómo está organizado tu catálogo en la tienda: el árbol de rubros, cuántas publicaciones cuelgan de cada uno (propias y de sus subrubros), cuáles están vacíos u ocultos y qué publicaciones quedaron sin rubro.',
    plataforma: 'tienda-nube',
    familia: 'catalogo',
    tipo: 'informacion',
    icono: '🗂️',
    Icon: FolderTree,
    component: () => import('./dashboards/RubrosTnDashboard.jsx'),
    orden: 13,
    // SIN `altoVolumen` — misma medición pendiente que el resto del módulo.
  },
]

// Dashboards PLANEADOS (30-sep-2026, pedido de Mariano: "de los que faltan
// sólo creá la tarjeta en otro tono para saber"). NO son dashboards: no tienen
// pantalla, no entran a la lista blanca ni a `vistasEmpresa` (no hay nada que
// gatear) y no se abren por URL — el hub sólo pinta su tarjeta, apagada, en su
// familia. `soloStaff`: es un mapa de trabajo interno, el cliente no la ve.
// Cuando uno se construye, su entrada se MUDA a `DASHBOARDS_TN` (con
// `component`) y se borra de acá.
export const DASHBOARDS_TN_PLANEADOS = [
  // Precios (30-sep-2026, decisión de Mariano): el dashboard NO se hace acá.
  // Compara el precio de cada producto entre TODOS los canales activos (hoy
  // Mercado Libre y Tienda Nube), así que vive en Omnicanalidad, una sola vez
  // — "para no hacerlo en distintos lugares". Un tablero de canal no muestra
  // datos de otro canal; por eso acá queda sólo la tarjeta, en la familia de
  // precios y promociones. 3-oct-2026: el de Mi negocio existe (`mn-precios`)
  // y esta tarjeta es un ENLACE a aquél (`enlace`): quien puede abrirlo la ve
  // viva y la toca para ir ahí (`tarjetaPlaneada` del hub); quien no, la sigue
  // viendo como planeada (sólo staff).
  {
    id: 'tn-precios',
    nombre: 'Precios',
    descripcion: 'Compará tu oferta en Mercado Libre y en tu tienda, producto por producto. Se abre en Mi negocio.',
    enlace: 'mn-precios',
    plataforma: 'tienda-nube',
    familia: 'precios-promociones',
    Icon: Scale,
    proximamente: true,
    soloStaff: true,
  },
]
